/**
 * GenericListView Component
 *
 * A complete list view component that encapsulates the entire ListView pattern:
 * - Collapsible header with title and icon
 * - Action bar with refresh and new item buttons
 * - Desktop table header and rows
 * - Mobile card layout
 * - Loading, error, and empty states
 * - Pagination (load more or full pagination)
 *
 * This is a higher-level component that composes ListView, ListActionBar,
 * ListHeader, ListRow, ListDesktopRow, ListMobileCard, and ListPagination.
 *
 * @example
 * ```tsx
 * <GenericListView
 *   title="Assessments"
 *   icon={<FileText size={20} />}
 *   expanded={expanded}
 *   onToggleExpand={toggleExpanded}
 *   items={assessments}
 *   keyExtractor={(item) => item.id}
 *   columns={[
 *     { key: 'date', label: 'Date', width: 'w-28', render: (item) => formatDate(item.date) },
 *     { key: 'name', label: 'Name', width: 'flex-1 min-w-0', render: (item) => item.name },
 *     { key: 'status', label: 'Status', width: 'w-24', align: 'center', render: (item) => <Badge>{item.status}</Badge> },
 *   ]}
 *   actions={[
 *     { icon: <Edit size={14} />, label: 'Edit', onClick: (item) => handleEdit(item) },
 *   ]}
 *   mobileCard={{
 *     primaryText: (item) => item.name,
 *     secondaryText: (item) => formatDate(item.date),
 *     badges: (item) => [{ label: item.status, className: getStatusColor(item.status) }],
 *   }}
 *   onRefresh={handleRefresh}
 *   refreshing={refreshing}
 *   loading={loading}
 *   newButtonLabel="New Assessment"
 *   newButtonOptions={assessmentTypeOptions}
 *   hasMore={hasMore}
 *   onLoadMore={handleLoadMore}
 *   loadingMore={loadingMore}
 *   error={error}
 *   onDismissError={() => setError(null)}
 *   emptyStateIcon={<FileText size={48} />}
 *   emptyStateTitle="No assessments available"
 *   emptyStateMessage="Start by creating a new assessment."
 * />
 * ```
 */
import React, { useState } from 'react';
import { Filter, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import ListView from './ListView';
import ListActionBar, { NewButtonOption } from './ListActionBar';
import ListHeader, { ColumnAlignment } from './ListHeader';
import ListRow from './ListRow';
import ListDesktopRow, { DesktopRowAction } from './ListDesktopRow';
import ListMobileCard, { MobileCardBadge, MobileCardAction } from './ListMobileCard';
import ListPagination from './ListPagination';
import ListSearchFilter, { FilterConfig } from './ListSearchFilter';

/** Column definition for the list */
export interface GenericListColumn<T> {
  /** Unique key for the column */
  key: string;
  /** Header label */
  label: string;
  /** CSS width class (e.g., 'w-28', 'flex-1 min-w-0') */
  width?: string;
  /** Text alignment */
  align?: ColumnAlignment;
  /** Render function for the cell content */
  render: (item: T) => React.ReactNode;
}

/** Action definition for desktop rows */
export interface GenericListAction<T> {
  /** Icon or visible content for the action */
  icon: React.ReactNode | ((item: T) => React.ReactNode);
  /** Action label (tooltip) */
  label: string | ((item: T) => string);
  /** Click handler */
  onClick: (item: T) => void;
  /** Optional CSS class */
  className?: string;
  /** Whether the action is visible (function or boolean) */
  isVisible?: ((item: T) => boolean) | boolean;
  /** Whether the action is disabled */
  disabled?: ((item: T) => boolean) | boolean;
}

/** Mobile card configuration */
export interface MobileCardConfig<T> {
  /** Primary text (main title) */
  primaryText: (item: T) => React.ReactNode;
  /** Secondary text (subtitle) */
  secondaryText?: (item: T) => React.ReactNode;
  /** Tertiary text (additional info) */
  tertiaryText?: (item: T) => React.ReactNode;
  /** Avatar/icon element */
  avatar?: (item: T) => React.ReactNode;
  /** Top right content */
  topRightContent?: (item: T) => React.ReactNode;
  /** Footer content */
  footer?: (item: T) => React.ReactNode;
  /** Badges to display */
  badges?: (item: T) => MobileCardBadge[];
  /** Actions (can be different from desktop actions) */
  actions?: (item: T) => MobileCardAction[];
}

export interface GenericListViewProps<T> {
  // ListView props
  /** Section title */
  title: string;
  /** Icon to display next to title */
  icon?: React.ReactNode;
  /** Whether the section is expanded */
  expanded: boolean;
  /** Callback to toggle expansion */
  onToggleExpand: () => void;
  /** Whether to hide the list header on desktop while preserving it on mobile */
  hideHeaderOnDesktop?: boolean;
  /** Whether to hide the list header at every breakpoint */
  hideHeader?: boolean;
  /** Alert banner to show at the top */
  alertBanner?: React.ReactNode;

  // Data props
  /** Array of items to display */
  items: T[];
  /** Function to extract a unique key from each item */
  keyExtractor: (item: T) => string;
  /** Column definitions */
  columns: GenericListColumn<T>[];
  /** Row actions */
  actions?: GenericListAction<T>[];
  /** Width class for the desktop actions column */
  actionsWidth?: string;
  /** Whether an item should receive the selected-row styling */
  isItemSelected?: (item: T) => boolean;
  /** Custom row renderer for lists that need expandable or otherwise bespoke rows */
  renderItem?: (item: T, index: number) => React.ReactNode;

  // Mobile card props
  /** Mobile card configuration */
  mobileCard: MobileCardConfig<T>;

  // Action bar props
  /** Refresh handler */
  onRefresh?: () => void;
  /** Whether data is being refreshed */
  refreshing?: boolean;
  /** Whether initial data is loading */
  loading?: boolean;
  /** Label for the new button */
  newButtonLabel?: string;
  /** Handler for new button click (simple mode) */
  onNewClick?: () => void;
  /** Options for new button dropdown */
  newButtonOptions?: NewButtonOption[];
  /** Whether the new button is disabled */
  newButtonDisabled?: boolean;
  /** Whether to show refresh button */
  showRefresh?: boolean;
  /** Whether to show new button */
  showNew?: boolean;
  /** Custom action bar content */
  actionBarChildren?: React.ReactNode;
  /** Align custom action bar content to the end of the toolbar */
  actionBarChildrenAlign?: 'start' | 'end';
  /** Whether to show the configured search control */
  showSearch?: boolean;
  /** Whether to show the configured filter controls */
  showFilters?: boolean;

  // Pagination props
  /** Whether there are more items to load */
  hasMore?: boolean;
  /** Handler for loading more items */
  onLoadMore?: () => void;
  /** Whether more items are loading */
  loadingMore?: boolean;
  /** Full pagination controls configuration */
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize?: number;
    totalCount?: number;
    onPageChange: (page: number) => void;
  };
  /** Whether to show pagination controls when there is only one page */
  showPaginationWhenSinglePage?: boolean;

  // State props
  /** Error message to display */
  error?: string | null;
  /** Handler to dismiss error */
  onDismissError?: () => void;
  /** Whether data has been loaded at least once */
  hasLoadedOnce?: boolean;

  // Empty state props
  /** Icon for empty state */
  emptyStateIcon?: React.ReactNode;
  /** Title for empty state */
  emptyStateTitle?: string;
  /** Message for empty state */
  emptyStateMessage?: string;
  /** Optional call to action under the empty state message (e.g. "Load sample patient") */
  emptyStateAction?: React.ReactNode;

  // Customization
  /** Additional content to render after the list (e.g., forms) */
  children?: React.ReactNode;
  /** Whether to show the list (set to false to show children only) */
  showList?: boolean;
  /** CSS class for the list container */
  className?: string;
  /** CSS class for the expanded list content area */
  contentClassName?: string;

  // Search and filter props
  /** Current search value */
  searchValue?: string;
  /** Search change handler */
  onSearchChange?: (value: string) => void;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Filter configurations */
  filters?: FilterConfig[];
  /** Number of results after filtering (for display) */
  resultCount?: number;
  /** Total count before filtering (for display) */
  filterTotalCount?: number;
  /** Storage key for persisting filter state */
  filterStorageKey?: string;
  /** Render filters inline with search on desktop */
  desktopInlineFilters?: boolean;
  /** Start desktop filters collapsed */
  desktopFiltersCollapsedByDefault?: boolean;
  /** Hide result count on desktop */
  hideDesktopFilterResultCount?: boolean;
}

function GenericListView<T>({
  // ListView props
  title,
  icon,
  expanded,
  onToggleExpand,
  hideHeaderOnDesktop = false,
  hideHeader = false,
  alertBanner,

  // Data props
  items,
  keyExtractor,
  columns,
  actions = [],
  actionsWidth = 'w-20',
  isItemSelected,
  renderItem,

  // Mobile card props
  mobileCard,

  // Action bar props
  onRefresh,
  refreshing = false,
  loading = false,
  newButtonLabel,
  onNewClick,
  newButtonOptions,
  newButtonDisabled = false,
  showRefresh = true,
  showNew = true,
  actionBarChildren,
  actionBarChildrenAlign = 'start',
  showSearch = true,
  showFilters = true,

  // Pagination props
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  pagination,
  showPaginationWhenSinglePage = false,

  // State props
  error,
  onDismissError,
  hasLoadedOnce = false,

  // Empty state props
  emptyStateIcon,
  emptyStateTitle = 'No items available',
  emptyStateMessage = 'Start by creating a new item using the button above.',
  emptyStateAction,

  // Customization
  children,
  showList = true,
  className = '',
  contentClassName = '',

  // Search and filter props
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  resultCount,
  filterTotalCount,
  filterStorageKey,
  desktopInlineFilters = false,
  desktopFiltersCollapsedByDefault = false,
  hideDesktopFilterResultCount = false,
}: GenericListViewProps<T>) {
  const [desktopFilterExpanded, setDesktopFilterExpanded] = useState(false);
  const configuredFilters = showFilters ? filters ?? [] : [];
  const hasSearch = showSearch && !!onSearchChange;
  const activeFilterCount = configuredFilters.filter((filter) => filter.value !== 'All' && filter.value !== '' && filter.value !== filter.options[0]?.value).length;
  // Convert GenericListAction to DesktopRowAction for a specific item
  const getDesktopActions = (item: T): DesktopRowAction[] => {
    return actions
      .filter((action) => {
        if (typeof action.isVisible === 'function') {
          return action.isVisible(item);
        }
        return action.isVisible !== false;
      })
      .map((action) => ({
        icon: typeof action.icon === 'function' ? action.icon(item) : action.icon,
        label: typeof action.label === 'function' ? action.label(item) : action.label,
        onClick: () => action.onClick(item),
        className: action.className,
        disabled: typeof action.disabled === 'function' ? action.disabled(item) : action.disabled,
      }));
  };

  // Get mobile actions for an item
  const getMobileActions = (item: T): MobileCardAction[] => {
    // If mobile card has custom actions, use those
    if (mobileCard.actions) {
      return mobileCard.actions(item);
    }

    // Otherwise, convert desktop actions
    return actions
      .filter((action) => {
        if (typeof action.isVisible === 'function') {
          return action.isVisible(item);
        }
        return action.isVisible !== false;
      })
      .map((action) => ({
        icon: typeof action.icon === 'function' ? action.icon(item) : action.icon,
        label: typeof action.label === 'function' ? action.label(item) : action.label,
        onClick: () => action.onClick(item),
        className: action.className,
      }));
  };

  return (
    <ListView
      title={title}
      icon={icon}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      hideHeaderOnDesktop={hideHeaderOnDesktop || desktopInlineFilters}
      hideHeader={hideHeader || desktopInlineFilters}
      alertBanner={alertBanner}
      contentClassName={contentClassName}
    >
      {/* Action Bar - only show when list is visible */}
      {showList && (
        <ListActionBar
          onRefresh={onRefresh}
          refreshing={refreshing}
          loading={loading}
          newButtonLabel={newButtonLabel}
          onNewClick={onNewClick}
          newButtonOptions={newButtonOptions}
          newButtonDisabled={newButtonDisabled}
          showRefresh={showRefresh}
          showNew={showNew}
          alignChildren={actionBarChildrenAlign}
          filterToggle={desktopInlineFilters && configuredFilters.length > 0 ? (
            <button type="button" onClick={() => setDesktopFilterExpanded(!desktopFilterExpanded)} className={`flex h-9 items-center justify-center px-1.5 rounded-md border transition-colors ${activeFilterCount > 0 ? 'bg-slate-700 text-white border-slate-700' : 'text-slate-600 border-slate-300 bg-white hover:bg-slate-100'}`} aria-expanded={desktopFilterExpanded} aria-label={desktopFilterExpanded ? 'Collapse filters' : 'Expand filters'} title={desktopFilterExpanded ? 'Collapse filters' : 'Expand filters'}>
              <Filter size={14} />
              {desktopFilterExpanded ? <ChevronUp size={14} className="ml-1" /> : <ChevronDown size={14} className="ml-1" />}
            </button>
          ) : undefined}
        >
          {desktopInlineFilters && (hasSearch || configuredFilters.length > 0) ? (
            <ListSearchFilter searchValue={searchValue} onSearchChange={hasSearch ? onSearchChange : undefined} searchPlaceholder={searchPlaceholder} filters={configuredFilters} desktopToolbarSearchOnly />
          ) : actionBarChildren}
        </ListActionBar>
      )}

      {/* Search and Filter - only show when list is visible and search/filters are configured */}
      {showList && (hasSearch || configuredFilters.length > 0) && (
        <ListSearchFilter
          searchValue={searchValue}
          onSearchChange={hasSearch ? onSearchChange : undefined}
          searchPlaceholder={searchPlaceholder}
          filters={configuredFilters}
          resultCount={resultCount}
          totalCount={filterTotalCount}
          loading={loading || refreshing}
          storageKey={filterStorageKey}
          desktopInline={false}
          desktopCollapsedByDefault={desktopFiltersCollapsedByDefault}
          hideDesktopResultCount={hideDesktopFilterResultCount}
          mobileOnly={desktopInlineFilters}
        />
      )}

      {showList && desktopInlineFilters && configuredFilters.length > 0 && (
        <ListSearchFilter
          filters={configuredFilters}
          desktopFilterPanelOnly
          desktopFilterExpanded={desktopFilterExpanded}
          onDesktopFilterExpandedChange={setDesktopFilterExpanded}
        />
      )}

      {/* List Content */}
      {showList && (
        <div className={`space-y-3 ${className}`}>
          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
              <p className="flex items-center">
                <span className="mr-2">⚠️</span> {error}
              </p>
              {onDismissError && (
                <button
                  onClick={onDismissError}
                  className="ml-4 text-red-500 hover:text-red-700 transition-colors p-1 rounded hover:bg-red-100"
                  title="Dismiss error"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Loading State */}
          {(loading || refreshing) && (
            <div className="flex justify-center items-center py-8">
              <Loader size={28} className="animate-spin text-slate-600" />
              <span className="ml-2 text-slate-600">
                {refreshing ? 'Refreshing' : 'Loading'}...
              </span>
            </div>
          )}

          {/* List Content */}
          {!loading && !error && (
            <>
              {/* Desktop Header */}
              {!renderItem && (
                <ListHeader
                  columns={columns.map((col) => ({
                    key: col.key,
                    label: col.label,
                    width: col.width,
                    align: col.align,
                  }))}
                  showActions={actions.length > 0}
                  actionsWidth={actionsWidth}
                />
              )}

              {/* List Rows */}
              {items.map((item, index) => (
                renderItem ? (
                  <React.Fragment key={keyExtractor(item)}>{renderItem(item, index)}</React.Fragment>
                ) : <ListRow
                  key={keyExtractor(item)}
                  isSelected={isItemSelected?.(item)}
                  desktopContent={
                    <ListDesktopRow
                      columns={columns.map((col) => ({
                        key: col.key,
                        width: col.width,
                        align: col.align,
                        content: col.render(item),
                      }))}
                      actions={getDesktopActions(item)}
                      actionsWidth={actionsWidth}
                    />
                  }
                  mobileContent={
                    <ListMobileCard
                      primaryText={mobileCard.primaryText(item)}
                      secondaryText={mobileCard.secondaryText?.(item)}
                      tertiaryText={mobileCard.tertiaryText?.(item)}
                      avatar={mobileCard.avatar?.(item)}
                      topRightContent={mobileCard.topRightContent?.(item)}
                      footer={mobileCard.footer?.(item)}
                      badges={mobileCard.badges?.(item) || []}
                      actions={getMobileActions(item)}
                    />
                  }
                />
              ))}

              {/* Empty State */}
              {hasLoadedOnce && items.length === 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                  {emptyStateIcon && (
                    <div className="mx-auto text-gray-300 mb-4">{emptyStateIcon}</div>
                  )}
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyStateTitle}</h3>
                  <p className="text-gray-500 text-sm">{emptyStateMessage}</p>
                  {emptyStateAction && <div className="mt-4 flex justify-center">{emptyStateAction}</div>}
                </div>
              )}

              {/* Pagination */}
              {pagination ? (
                <ListPagination
                  mode="pagination"
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  pageSize={pagination.pageSize}
                  totalCount={pagination.totalCount}
                  onPageChange={pagination.onPageChange}
                  loading={loading}
                  showWhenSinglePage={showPaginationWhenSinglePage}
                />
              ) : null}
              {onLoadMore ? (
                <ListPagination
                  mode="loadMore"
                  hasMore={hasMore && !loading}
                  onLoadMore={onLoadMore}
                  loading={loadingMore}
                />
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Additional content (e.g., forms) */}
      {children}
    </ListView>
  );
}

export default GenericListView;
